import { clamp } from '../math.ts';
import { resolveElevationStack } from './elevationStack.ts';
import type { Island, MapDocument, Rock } from './types.ts';

export type ElevatedPaintBody =
  | { kind: 'island'; body: Island }
  | { kind: 'rock'; body: Rock };

/**
 * Islands and rocks share one painter list. Phaser depth is per Graphics object, so two
 * layers cannot interleave: a high island would always sit under every rock. Sort low
 * world elevation first; at the same height islands stay under rocks and document order
 * is kept (Array.sort is stable).
 */
export function elevatedBodiesInPaintOrder(map: MapDocument): ElevatedPaintBody[] {
  const stack = resolveElevationStack(map);
  const tagged: ElevatedPaintBody[] = [
    ...map.islands.map((body) => ({ kind: 'island' as const, body })),
    ...map.rocks.map((body) => ({ kind: 'rock' as const, body })),
  ];

  tagged.sort((a, b) => {
    const elevA = stack.get(a.body.id)?.worldElevation ?? a.body.elevation;
    const elevB = stack.get(b.body.id)?.worldElevation ?? b.body.elevation;

    if (elevA !== elevB) {
      return elevA - elevB;
    }

    if (a.kind === b.kind) {
      return 0;
    }

    return a.kind === 'island' ? -1 : 1;
  });

  return tagged;
}

/**
 * Display-list step inside the land band (play 9.5–9.9, editor 2.x). Each body
 * occupies two slots so a later column can cover an earlier one. Visible props
 * and pole shafts sit in a shared Y-sort band above the last column
 * (`ySortDepth`). Hidden props are culled.
 */
export const ELEVATED_DEPTH_STEP = 0.004;

export function elevatedColumnDepth(base: number, index: number): number {
  return base + index * 2 * ELEVATED_DEPTH_STEP;
}

export function elevatedPropDepth(base: number, index: number): number {
  return elevatedColumnDepth(base, index) + ELEVATED_DEPTH_STEP;
}

/** Above every column so a palm in front of a cliff is not clipped by it. */
export function elevatedFrontPropDepth(base: number, bodyCount: number): number {
  return elevatedColumnDepth(base, Math.max(0, bodyCount));
}

/**
 * Painter sort for props and checkpoint/goal shafts. World y grows south / down
 * the screen; a larger y draws in front. `y` is the ground contact (prop plant,
 * pole base), not the extruded sprite top. Clamp so a stamp just outside the
 * map still sits in the band.
 */
export function ySortDepth(base: number, y: number, worldHeight: number, span: number): number {
  const height = Math.max(1, worldHeight);
  return base + clamp(y / height, 0, 1) * span;
}
